import React, { FunctionComponent } from 'react';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { push, CallHistoryMethodAction } from 'connected-react-router';

import styled from 'styled-components';

import { motion } from 'framer-motion';

import Logo from '@components/basics/logo';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Button from '@components/actions/button';
import { opacityMotion } from '@components/framer';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';

import * as optIn from '@webapp/sdk/opt-in';
import { OptInRequest } from '@services/opt-in/types/api/opt-in-request';
import CommunityImage from './img/community.jpg';
import LogoLoadingCtrl from '@webapp/controllers/loader-modal/logo';
import { rudderTrack } from '@webapp/utils/rudderstack/init';

const EmailTemplate =
  //eslint-disable-next-line
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface CommunityLandingPageProps {
  className?: string;
  loggedIn?: boolean;
  goTo: (url: string) => CallHistoryMethodAction;
}

const ITEMS = [
  {
    title: 'COMMUNITY.LANDING.ITEMS.WEB3.TITLE',
    text: 'COMMUNITY.LANDING.ITEMS.WEB3.TEXT',
  },
  {
    title: 'COMMUNITY.LANDING.ITEMS.COMMUNITY.TITLE',
    text: 'COMMUNITY.LANDING.ITEMS.COMMUNITY.TEXT',
  },
  {
    title: 'COMMUNITY.LANDING.ITEMS.EDUCATION.TITLE',
    text: 'COMMUNITY.LANDING.ITEMS.EDUCATION.TEXT',
  },
];

const FEATURE_NAME = 'community';

const mapStateToProps = (state: AppState) => ({
  loggedIn: state.auth.loggedIn,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goTo: (url: string) => dispatch(push(`${url}`)),
});

const CommunityLandingPage: FunctionComponent<CommunityLandingPageProps> = ({
  className,
  loggedIn,
  goTo,
}) => {
  const [email, setEmail] = React.useState<string>('');
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const validateEmail = (email: string): boolean => {
    return EmailTemplate.test(String(email).toLowerCase());
  };
  const isEmailUsed = async (
    email: string,
    feature: string
  ): Promise<boolean> => {
    const checkEmailOptIn = await optIn.checkContact(email, feature);
    return checkEmailOptIn.optIn;
  };

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  React.useEffect(() => {
    let timer;
    if (submitted) {
      timer = setTimeout(() => {
        if (loggedIn) {
          goTo('/dashboard');
        } else {
          window.open('https://trac.co/', '_self');
        }
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [loggedIn, submitted, goTo]);

  const handleBlurEmail = async () => {
    const invalidEmail = !validateEmail(email);
    const emailUsed = await isEmailUsed(email, FEATURE_NAME);
    if (invalidEmail) {
      setError('COMMUNITY.LANDING.INPUT.ERROR.INVALID');
    } else if (emailUsed) {
      setError('COMMUNITY.LANDING.INPUT.ERROR.USED');
    } else {
      setError(undefined);
    }
  };

  const handleChange = (value: string) => {
    setError(undefined);
    setEmail(value);
  };

  const handleSubmit = async () => {
    const invalidEmail = !validateEmail(email);
    const emailUsed = await isEmailUsed(email, FEATURE_NAME);
    if (invalidEmail) {
      setError('COMMUNITY.LANDING.INPUT.ERROR.INVALID');
    } else if (emailUsed) {
      setError('COMMUNITY.LANDING.INPUT.ERROR.USED');
    } else {
      const payload: OptInRequest = {
        feature: FEATURE_NAME,
        contact: email,
        contactType: 'email',
      };
      await optIn.optIn(payload);
      setError(undefined);
      setIsLoading(true);
      setSubmitted(true);
      await rudderTrack('Subscribed', { user_email_address: email });
    }
  };

  return (
    <div className={className || ''}>
      <Header>
        <Logo className="logo" color="pink" />
      </Header>
      <Hero>
        <Info>
          <Typography className="hero-title" customFont="everett-medium">
            <Translate id="COMMUNITY.LANDING.TITLE" />
          </Typography>
          <Typography className="hero-text" type="subheader" weight="400">
            <Translate id="COMMUNITY.LANDING.COPY" />
          </Typography>
          <Flex className="form" gap={10}>
            {submitted ? (
              <MotionOpacity className="hero-input placeholder">
                <Typography type="small">
                  <Translate id="COMMUNITY.LANDING.INPUT.JOINED.TITLE" />
                </Typography>
                <Typography weight="700">
                  <Translate id="COMMUNITY.LANDING.INPUT.JOINED.TEXT" />
                </Typography>
              </MotionOpacity>
            ) : (
              <InputField
                className="hero-input"
                isDark
                label={<Translate id="COMMUNITY.LANDING.INPUT.LABEL" />}
                value={email}
                onChange={handleChange}
                onBlur={handleBlurEmail}
                // TODO: DEVTEAM-996 - Integrate errors if the email is already in use or if it's invalid
                error={error && <Translate id={error} />}
              />
            )}
            <Button
              buttonType={
                !email || submitted || isLoading ? 'blacked' : 'primary'
              }
              disabled={!email || submitted || isLoading}
              className={`hero-button ${isLoading ? 'loading' : ''}`}
              onClick={handleSubmit}
            >
              <Translate
                id={
                  isLoading
                    ? 'COMMUNITY.LANDING.INPUT.LOADING'
                    : submitted
                    ? 'COMMUNITY.LANDING.INPUT.JOINED.BUTTON'
                    : 'COMMUNITY.LANDING.INPUT.BUTTON'
                }
              />
              {isLoading && (
                <LogoLoadingCtrl className="loading-icn" loading={isLoading} />
              )}
            </Button>
          </Flex>
        </Info>
        <Image
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={opacityMotion}
          className="image"
          src={CommunityImage}
        />
      </Hero>
      <ItemsWrapper>
        {ITEMS.map((item, i) => (
          <Item key={i}>
            <Typography weight="700">
              <Translate id={item.title} />
            </Typography>
            <Typography>
              <Translate id={item.text} />
            </Typography>
          </Item>
        ))}
      </ItemsWrapper>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(CommunityLandingPage)`
  min-height: 100vh;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 24px 80px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    padding: 0;
  }
`);

const Header = styled.div`
  align-items: center;
  padding: 40px 0;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    padding: 48px 0 10px;
    grid-template-columns: repeat(24, minmax(0, 1fr));
  }

  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    padding-bottom: 32px;
  }
  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    padding-bottom: 10px;
  }

  .logo {
    margin: 0 auto;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin: 0;
      grid-column: 2 / 4;
    }
    ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
      grid-column: span 3;
    }
  }
`;

const Hero = styled.div`
  align-items: center;
  margin-bottom: 40px;
  display: grid;
  grid-template-columns: 1fr;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    grid-template-columns: repeat(24, minmax(0, 1fr));
    grid-gap: 10px;
    margin-bottom: 80px;
  }
`;

const Info = styled(MotionOpacity)`
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    grid-column: 3 / 13;
    grid-row: 1;
  }

  .hero-title {
    color: ${({ theme }) => theme.colors.white};
    font-size: ${({ theme }) => theme.typography.h2Bigger};
    line-height: 100%;
    margin-bottom: 20px;

    ${({ theme }) => `@media screen and ${theme.device.mobileM}`} {
      font-size: ${({ theme }) => theme.typography.heroMobile};
    }
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      font-size: ${({ theme }) => theme.typography.hero};
      margin-bottom: 24px;
      line-height: 94%;
    }
  }

  .hero-text {
    font-size: ${({ theme }) => theme.typography.span};
    color: ${({ theme }) => theme.colors.pink};
    margin-bottom: 24px;

    ${({ theme }) => `@media screen and ${theme.device.mobileM}`} {
      font-size: ${({ theme }) => theme.typography.h4};
    }

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin-bottom: 32px;
    }
  }

  .form {
    align-items: center;
    flex-direction: column;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      flex-direction: row;
    }

    .hero-input {
      flex-grow: 1;
      width: 100%;
      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        width: 60%;
      }

      &.placeholder {
        opacity: 0.5;
        display: grid;
        align-items: center;
        height: 48px;
        span {
          color: ${({ theme }) => theme.colors.pink};
        }
      }
    }

    .hero-button {
      flex-grow: 1;
      position: relative;
      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        width: 40%;
        height: 60px;
        max-height: none;
      }

      &.loading {
        text-align: left;
      }

      .loading-icn {
        width: 24px !important;
        height: 24px !important;
        position: absolute;
        top: 50%;
        right: 24px;
        left: auto;
        transform: translate(0, -50%);
      }
    }
  }
`;

const Image = styled(motion.img)`
  width: 70%;
  grid-row: -1;
  margin: 0 auto 56px;
  ${({ theme }) => `@media screen and ${theme.device.mobileM}`} {
    width: 100%;
  }
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    grid-row: 1;
    margin-bottom: 0;
    margin-left: auto;
    margin-right: 0;
    grid-column: 15 / 24;
  }
`;

const ItemsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 32px;
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    grid-template-columns: repeat(24, minmax(0, 1fr));
    grid-gap: 10px;
  }
`;

const Item = styled.div`
  display: grid;
  grid-gap: 10px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    &:nth-child(1) {
      grid-column: 3 / 9;
    }
    &:nth-child(2) {
      grid-column: 10 / 16;
    }
    &:nth-child(3) {
      grid-column: 17 / 23;
    }
  }

  span {
    color: ${({ theme }) => theme.colors.pink};
    font-size: ${({ theme }) => theme.typography.h3};
  }
`;
